import React, {useEffect, useReducer, useState} from 'react'
import {useParams} from 'react-router-dom'
import {useSolv, useTenant} from "../components/SolvProvider"
import {useIntlEx} from "../components/IntlUtils"
import {FormBody, FormGroup, FormHeader, Icon, MainContainer, NumericInput} from "../components/FormComps.js"
import {clearAllErrors, InfoBlock, InfoText, validateField} from '../components/ValidationUtils.js';
import {simpleReducer} from "../components/ReactUtils";
import MainMenu from "../MainMenu"
import {default as Select} from "react-select";
import {AsyncPaginate} from "react-select-async-paginate";
import getLogger from "../components/Logging.js"
import '../App.css'

const log = getLogger("Location")

export default function Location(props) {

  const {api, session: {user}, refreshSignin, setFatal, setBusy} = useSolv()
  const {intl } = useIntlEx()

  const statusOpts = [
    {
      value: "ACTIVE",
      label: intl.msg("status_active")
    },
    {
      value: "INACTIVE",
      label: intl.msg("status_inactive")
    },
  ]

  function getStatusOpts() {
    return (statusOpts)
  }

  function getStatusOpt(status) {
    return (statusOpts.find(i => i.value === status))
  }

  const params = useParams()

  const {tenant} = useTenant(params.tenantId)

  const [locationId, setLocationId] = useState(null)
  const [isAdding, setAdding] = useState(null)

  const [touched, setTouched] = useState(false)
  const [alert, setAlert] = useState(null)

  const [location, updateLocation] = useReducer(simpleReducer, {})
  const [locationType, setLocationType] = useState(null)
  const [coords, setCoords] = useState(null)
  const [country, setCountry] = useState(null)
  const [status, setStatus] = useState(getStatusOpt("ACTIVE"))

  useEffect(() => {
    if (params.locationId) {
      if (params.locationId.trim().toLowerCase() === "_new") {
        setLocationId(null)
        setLocationType({value: `UNDEF`, label: `Undefined`})
        setCountry({value: `??`, label: `Undefined`})
        setAdding(true)
      }
      else {
        setLocationId(params.locationId)
        setLocationType({value: `UNDEF`, label: `Undefined`})
        setCountry({value: `??`, label: `Undefined`})
        setAdding(false)
      }
    }
  }, [])

  useEffect(() => {

    // log.debug("useEffect: invoked")

    if (locationId) {
      setBusy(intl.msg("loading"))
      api.getLocation(locationId)
        .then(({data}) => {
          // log.debug("getLocation: data=", data)
          updateLocation(data)
          setLocationType({value: data.locationTypeId, label: data.locationTypeName})
          setCoords(makeCoords(data.lat, data.lng, data.radius))
          setCountry({value: data.countryCode, label: `${data.countryName} (${data.countryCode})`})
          setStatus(getStatusOpt(data.status))
        })
        .catch((error) => {
          log.error(error)
          setFatal(error)
        })
        .finally(() => {
          setBusy(null)
        })
    }

  }, [locationId]);

  function handleLocationNameChange(e) {
    updateLocation({locationName: e.target.value?.trim()})
    setTouched(true)
  }

  function handleDisplayNameChange(e) {
    updateLocation({displayName: e.target.value?.trim()})
    setTouched(true)
  }

  function handleLocationTypeChange(e) {
    updateLocation({locationTypeId: e.value?.trim()})
    setLocationType(e)
    setTouched(true)
  }

  function handleCoordsChange(e) {
    const v = e.target.value
    // const [latitude, longitude, radius] = parseCoors(v)
    // updateLocation({lat: latitude, lng: longitude, radius: radius})
    setCoords(v)
    setTouched(true)
  }

  function handleCoordsBlur(e) {
    const v = e.target.value
    try {
      const [latitude, longitude, radius] = parseCoors(v)
      // updateLocation({lat: latitude, lng: longitude, radius: radius})
      const c = makeCoords(latitude, longitude, radius)
      setCoords(c)
    }
    catch (e) {
      setCoords("")
    }
    setTouched(true)
  }

  function handleCountryChange(e) {
    updateLocation({countryCode: e.value?.trim()})
    setCountry(e)
    setTouched(true)
  }

  function handlePopChange(v, s) {
    updateLocation({pop: v})
    setTouched(true)
  }

  function handleLatChange(v, s) {
    updateLocation({lat: v})
    setTouched(true)
  }

  function handleLngChange(v) {
    updateLocation({lng: v})
    setTouched(true)
  }

  function handleRadiusChange(v) {
    updateLocation({radius: v})
    setTouched(true)
  }

  function handleStatusChange(v) {
    setStatus(v)
    setTouched(true)
  }

  function loadLocationTypes(input, loadedOptions, additional) {
    return api.listLocationTypes({view: "BASIC", extraParams: `${input ? `filter=locationTypeName:${encodeURIComponent(input)}&` : ""}sort=locationTypeName:ASC`, cursor: additional?.cursor, limit: 20})
      .then(({data, nextCursor}) => {
        // log.debug("loadCountries: data=", data)
        let opt = data.map(c => {
          return {
            value: c.locationTypeId,
            label: c.locationTypeName,
          };
        });
        let res = {
          options: opt,
        }
        if (nextCursor) {
          res = {
            ...res,
            hasMore: true,
            additional: {
              cursor: nextCursor
            }
          }
        }
        // log.debug("loadClients: res=", res)
        return res
      });
  }

  function loadCountries(input, loadedOptions, additional) {
    return api.listCountries({view: "BASIC", extraParams: `${input ? `filter=countryName:${encodeURIComponent(input)}&` : ""}sort=countryName:ASC`, cursor: additional?.cursor, limit: 20})
      .then(({data, nextCursor}) => {
        // log.debug("loadCountries: data=", data)
        let opt = data.map(c => {
          return {
            value: c.countryCode,
            label: c.countryCode,
            pop: c.pop
          };
        });
        let res = {
          options: opt,
        }
        if (nextCursor) {
          res = {
            ...res,
            hasMore: true,
            additional: {
              cursor: nextCursor
            }
          }
        }
        // log.debug("loadClients: res=", res)
        return res
      });
  }

  function validate() {

    let opt = {focusEl: null, valid: true}

    clearAllErrors("frmMain")

    validateField("inp_locationName", location.locationName && location.locationName.trim().length > 0 , "required", opt)
    validateField("inp_displayName", location.displayName && location.displayName.trim().length > 0 , "required", opt)
    validateField("inp_pop", location.pop && (parseInt(location.pop) != NaN) && (location.pop > 0) , "required", opt)
    validateField("inp_coords", coords && coords.trim().length > 0 , "required", opt)

    try {
      const [latitude, longitude, radius] = parseCoors(coords)
    }
    catch (e) {
      validateField("inp_coords", false , "valid", opt)
    }

    // log.debug("VALIDATE: opt=", opt)

    if (opt.focusEl) {
      document.getElementById(opt.focusEl).focus()
    }

    return opt.valid

  }

  async function handleSaveClick() {

    // log.debug("handleSaveClick: invoked" )

    if (validate()) {

      setBusy(intl.msg("saving"))

      try {

        const [latitude, longitude, radius] = parseCoors(coords)

        if (isAdding) {

          const payload = {
            locationName: location.locationName,
            locationTypeId: locationType.value,
            displayName: location.displayName,
            countryCode: country.value,
            pop: location.pop,
            lat: latitude,
            lng: longitude,
            radius: radius,
            status: status.value,
          }

          // log.debug("saveLocation: payload=", payload)

          const {data} = await api.insertLocation(payload)

        }
        else {

          const payload = {
            locationTypeId: locationType.value,
            locationName: location.locationName,
            displayName: location.displayName,
            countryCode: country.value,
            pop: location.pop,
            lat: latitude,
            lng: longitude,
            radius: radius,
            status: status.value,
          }

          log.debug("saveLocation: payload=", payload)

          const {data} = await api.updateLocation(locationId, payload)

        }

        window.location = `/sys/locations`

      }
      catch (error) {
        handleError(error)
      }
      finally {
        setBusy(null)
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_validation")})
    }

  }

  function handleCancelClick() {
    window.location.reload()
  }

  function handleError(error) {
    if (error && error.code) {
      // log.debug("handleError: error=", error.code)
      const code = error.code
        setAlert({type: "error", text: intl.msg("error_failed")})
    }
    else {
      setAlert({type: "error", text: intl.msg("error_failed")})
    }
  }

  function isEditable() {
    return user.isSystem() && user.isManagerOrAbove()
  }

  function parseCoors(input) {
    if (typeof input !== 'string') return null;

    // Split the input by comma
    const parts = input.split(',').map(p =>
      // Keep only digits, dot, minus sign
      p.replace(/[^0-9.-]/g, '')
    );

    // Parse floats
    let latitude = parseFloat(parts[0]);
    let longitude = parseFloat(parts[1]);
    const radius = parts[2] !== undefined ? parseFloat(parts[2]) : 10;

    // Check if latitude and longitude are valid numbers
    if (isNaN(latitude) || isNaN(longitude)) return null;

    // Truncate to 6 decimal places (without rounding)
    latitude = Math.trunc(latitude * 1e6) / 1e6;
    longitude = Math.trunc(longitude * 1e6) / 1e6;

    return [latitude, longitude, radius];
  }

  function makeCoords(latitude, longitude, radius = 10) {
    return `${latitude},${longitude},${radius}`
  }

  function googleMapsLink() {
    try {
      const [latitude, longitude, radius] = parseCoors(coords)
      // updateLocation({lat: latitude, lng: longitude, radius: radius})
      return `http://maps.google.com/maps?z=12&t=m&q=loc:${latitude}+${longitude}`
    }
    catch (e) {
      return `http://maps.google.com/maps`
    }

  }

  return (
    tenant &&
      <>
        <MainContainer tenant={tenant} menu={MainMenu} allowIfAccessingAs={"*/*/SYSTEM"}>

          <FormHeader>
            <FormHeader.Toolbar>
              <FormHeader.Toolbar.Title>
                {intl.msg("location_title")} {" - " + (!locationId && isAdding ? "(new)" : location.locationName)}
              </FormHeader.Toolbar.Title>
              <FormHeader.Toolbar.Controls>
                {
                  touched &&
                    <>
                      <button key="key_btn_save" className="btn btn-primary" onClick={handleSaveClick}>
                        <i className="fas fa-check"></i>{intl.msg("save")}
                      </button>
                      <button key="key_btn_reset" className="btn btn-secondary" onClick={handleCancelClick}>
                        <i className="fas fa-times"></i>{intl.msg("cancel")}
                      </button>
                    </>
                }
                {/*<MoreButton/>*/}
              </FormHeader.Toolbar.Controls>
            </FormHeader.Toolbar>
            <FormHeader.Alert alert={alert}/>
          </FormHeader>

          <FormBody>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_country" text={intl.msg("country")} description={intl.msg("country_description")}/>
              <FormGroup.Controls>
                <div style={{maxWidth: "600px"}}>
                  <AsyncPaginate
                    id="inp_country"
                    className="react-select"
                    classNamePrefix="react-select"
                    cacheOptions
                    value={country}
                    debounceTimeout={800}
                    defaultOptions={true}
                    loadOptions={loadCountries}
                    onChange={handleCountryChange}
                    isDisabled={!isEditable()}/>
                </div>
                <InfoBlock disabled={!isEditable()}>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_locationType" text={intl.msg("location_type")} description={intl.msg("location_type_description")}/>
              <FormGroup.Controls>
                <div style={{maxWidth: "600px"}}>
                  <AsyncPaginate
                    id="inp_locationType"
                    className="react-select"
                    classNamePrefix="react-select"
                    cacheOptions
                    value={locationType}
                    debounceTimeout={800}
                    defaultOptions={true}
                    loadOptions={loadLocationTypes}
                    onChange={handleLocationTypeChange}
                    isDisabled={!isEditable()}/>
                </div>
                <InfoBlock disabled={!isEditable()}>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_locationName" text={intl.msg("location_name")} description={intl.msg("location_name_description")}/>
              <FormGroup.Controls>
                <input id="inp_locationName" type="email" className="form-control" value={location.locationName} disabled={!isEditable()} onChange={handleLocationNameChange}/>
                <InfoBlock disabled={!isEditable()}>
                  <InfoText validate="unique">{intl.msg("location_name_helptext_unique")}</InfoText>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_displayName" text={intl.msg("location_display_name")} description={intl.msg("location_display_name_description")}/>
              <FormGroup.Controls>
                <input id="inp_displayName" type="email" className="form-control" value={location.displayName} disabled={!isEditable()} onChange={handleDisplayNameChange}/>
                <InfoBlock disabled={!isEditable()}>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_pop" text={intl.msg("location_pop")} description={intl.msg("location_pop_description")}/>
              <FormGroup.Controls>
                <div style={{width: "200px"}}>
                  <NumericInput id="inp_pop" className="form-control" min={0} disabled={!isEditable()} value={location.pop} onChange={handlePopChange}/>
                </div>
                <InfoBlock disabled={!isEditable()}>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_coords" text={intl.msg("location_coords")} description={intl.msg("location_coords_description")}/>
              <FormGroup.Controls>
                <div className="d-flex d-flex-row" style={{gap: "4px"}}>
                  <div style={{width: "300px"}}>
                    <input id="inp_coords" type="text" className="form-control" value={coords} disabled={!isEditable()} onChange={handleCoordsChange} onBlur={handleCoordsBlur}/>
                  </div>
                  <a className="btn btn-secondary" style={{width: "30px", padding: "6px"}} href={googleMapsLink()} target="_blank">
                    <Icon name="compass"/>
                  </a>
                </div>
                <InfoBlock disabled={!isEditable()}>
                  <InfoText validate="valid">{intl.msg("location_coords_helptext")}</InfoText>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

            {/*<FormGroup>*/}
            {/*  <FormGroup.Label htmlFor="inp_lat" text={intl.msg("location_lat_lng")} description={intl.msg("location_lat_lng_description")}/>*/}
            {/*  <FormGroup.Controls>*/}
            {/*    <div className="d-flex d-flex-row" style={{gap: "4px"}}>*/}
            {/*      <NumericInput id="inp_lat" className="form-control" min={-999.999999} max={999.999999} decimals={6} disabled={!isEditable()} value={location.lat} onChange={handleLatChange}/>*/}
            {/*      <NumericInput id="inp_lng" className="form-control" min={-999.999999} max={999.999999} decimals={6} disabled={!isEditable()} value={location.lng} onChange={handleLngChange}/>*/}
            {/*      <a className="btn btn-secondary" style={{width: "30px", padding: "6px"}} href={`http://maps.google.com/maps?z=12&t=m&q=loc:${location.lat}+${location.lng}`} target="_blank">*/}
            {/*        <Icon name="compass"/>*/}
            {/*      </a>*/}
            {/*    </div>*/}
            {/*    <InfoBlock disabled={!isEditable()}>*/}
            {/*      <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>*/}
            {/*    </InfoBlock>*/}
            {/*  </FormGroup.Controls>*/}
            {/*</FormGroup>*/}

            {/*<FormGroup>*/}
            {/*  <FormGroup.Label htmlFor="inp_radius" text={intl.msg("location_radius")} description={intl.msg("location_radius_description")}/>*/}
            {/*  <FormGroup.Controls>*/}
            {/*    <div style={{maxWidth: "200px"}}>*/}
            {/*      <div className="d-flex d-flex-row" style={{gap: "4px"}}>*/}
            {/*        <NumericInput id="inp_radius" className="form-control" min={5} max={999999999.00} decimals={2} disabled={!isEditable()} value={location.radius} onChange={handleRadiusChange}/>*/}
            {/*        <div className="d-flex justify-content-center align-items-center">km</div>*/}
            {/*      </div>*/}
            {/*    </div>*/}
            {/*    <InfoBlock disabled={!isEditable()}>*/}
            {/*      <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>*/}
            {/*    </InfoBlock>*/}
            {/*  </FormGroup.Controls>*/}
            {/*</FormGroup>*/}

            <FormGroup>
              <FormGroup.Label htmlFor="inp_status" text={intl.msg("status")} description={intl.msg("status_description")}/>
              <FormGroup.Controls>
                <div style={{maxWidth: "200px"}}>
                  <Select
                    id="inp_status"
                    className="react-select"
                    classNamePrefix="react-select"
                    value={status}
                    options={getStatusOpts(intl)}
                    onChange={handleStatusChange}
                    isDisabled={!isEditable()}/>
                </div>
                <InfoBlock disabled={!isEditable()}>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

          </FormBody>
        </MainContainer>

      </>

  )
}
